import React, { useEffect, useState } from "react";
import axios from "utils/axiosConfig";
import { useSelector, useDispatch } from "react-redux";
import uri from "../../features/config";
import AdminLayout from "../AdminLayout";
import {
  HiOutlinePlus,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineTicket,
} from "react-icons/hi";
import { useToast } from "../../components/Toast";
import { getPackages } from "../../features/credits/creditsSlice";

import SidePanel from "../../components/SidePanel";

const CreditManagement = () => {
  const toast = useToast();
  const { user } = useSelector((state) => state.auth);
  const { packages, isLoading } = useSelector((state) => state.credits);
  const dispatch = useDispatch();

  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    credits: "",
    price: "",
    bonus: "",
  });

  useEffect(() => {
    dispatch(getPackages());
  }, [dispatch]);

  const resetForm = () => {
    setFormData({ name: "", credits: "", price: "", bonus: "" });
    setEditingPackage(null);
  };

  const handleEdit = (pkg) => {
    setEditingPackage(pkg);
    setFormData({
      name: pkg.name || "",
      credits: pkg.credits || "",
      price: pkg.price || "",
      bonus: pkg.bonus || "",
    });
    setIsPanelOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPackage) {
        await axios.put(
          `${uri}/api/admin/credits/${editingPackage._id}`,
          formData,
          {
            headers: { "x-user-email": user?.data?.email },
          }
        );
        toast.success("Matrix package synchronized");
      } else {
        await axios.post(`${uri}/api/admin/credits`, formData, {
          headers: { "x-user-email": user?.data?.email },
        });
        toast.success("New credit module initialized");
      }
      setIsPanelOpen(false);
      resetForm();
      dispatch(getPackages());
    } catch (error) {
      toast.error("Module initialization failed");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Purge this credit module from the matrix?")) return;
    try {
      await axios.delete(`${uri}/api/admin/credits/${id}`, {
        headers: { "x-user-email": user?.data?.email },
      });
      toast.success("Module purged successfully");
      dispatch(getPackages());
    } catch (error) {
      toast.error("Module purge failed");
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-12 animate-fadeIn">
        <header className="flex flex-col md:flex-row md:items-end justify-between gap-8 relative">
          <div className="space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-amber-500/10 border border-amber-500/20 rounded-full text-[10px] font-black text-amber-400 uppercase tracking-widest">
              Economy_Matrix_Status: Stable
            </div>
            <h1 className="text-4xl md:text-6xl font-black text-white uppercase italic tracking-tighter">
              Credit <span className="text-amber-500">Inventory</span>
            </h1>
            <p className="text-gray-500 font-bold uppercase tracking-[0.2em] text-[10px]">
              Gamer dynamic point packages and tactical monetization control.
            </p>
          </div>
          <button
            onClick={() => {
              resetForm();
              setIsPanelOpen(true);
            }}
            className="group relative flex items-center justify-center gap-3 px-8 py-5 bg-amber-500 text-black font-black rounded-2xl hover:bg-amber-400 transition-all shadow-[0_0_25px_rgba(245,158,11,0.2)] transform active:scale-95"
          >
            <HiOutlinePlus
              size={22}
              className="group-hover:rotate-90 transition-transform duration-300"
            />
            <span className="uppercase tracking-widest italic text-xs">
              Initialize Package
            </span>
          </button>
        </header>

        {isLoading ? (
          <div className="py-24 text-center animate-pulse">
            <p className="text-amber-400 font-black uppercase tracking-[0.4em] italic text-xs">
              Synchronizing_Economy_Data...
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {packages.map((pkg) => (
              <div
                key={pkg._id}
                className="bg-[#111827]/40 backdrop-blur-xl p-10 rounded-[3rem] border border-white/10 flex flex-col justify-between group hover:border-amber-500/30 transition-all duration-500 h-full relative overflow-hidden shadow-2xl"
              >
                {/* Decorative Background Icon */}
                <div className="absolute -right-8 -top-8 text-white/[0.02] rotate-12 scale-150 pointer-events-none group-hover:text-amber-500/[0.03] transition-colors">
                  <HiOutlineTicket size={240} />
                </div>

                <div className="flex items-start justify-between mb-10 relative z-10">
                  <div className="bg-amber-500/10 p-5 rounded-2xl text-amber-500 border border-amber-500/20 shadow-inner group-hover:scale-110 transition-transform duration-500">
                    <HiOutlineTicket size={32} />
                  </div>
                  <div className="flex gap-3 opacity-0 group-hover:opacity-100 transform translate-y-2 group-hover:translate-y-0 transition-all duration-500">
                    <button
                      onClick={() => handleEdit(pkg)}
                      className="w-12 h-12 flex items-center justify-center bg-white/5 hover:bg-white/10 border border-white/10 text-cyan-400 rounded-2xl transition-all"
                    >
                      <HiOutlinePencil size={20} />
                    </button>
                    <button
                      onClick={() => handleDelete(pkg._id)}
                      className="w-12 h-12 flex items-center justify-center bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 text-red-500 rounded-2xl transition-all"
                    >
                      <HiOutlineTrash size={20} />
                    </button>
                  </div>
                </div>

                <div className="relative z-10">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="px-3 py-1 bg-white/5 text-gray-500 text-[9px] font-black rounded-lg border border-white/5 uppercase tracking-[0.2em] italic font-mono">
                      ID: {pkg._id}
                    </span>
                  </div>
                  <h3 className="text-3xl font-black text-white mb-3 italic tracking-tight group-hover:text-amber-400 transition-colors">
                    {pkg.name}
                  </h3>
                  <div className="flex items-baseline gap-2">
                    <span className="text-5xl font-black text-white tracking-tighter italic">
                      ${pkg.price}
                    </span>
                    <span className="text-[10px] text-gray-600 font-black uppercase tracking-[0.3em]">
                      / Module
                    </span>
                  </div>

                  <div className="mt-10 grid grid-cols-2 gap-4">
                    <div className="p-5 bg-white/5 rounded-2xl border border-white/5 group-hover:border-amber-500/10 transition-colors">
                      <p className="text-[9px] font-black text-gray-500 uppercase tracking-[0.3em] mb-2 italic">
                        Point_Value
                      </p>
                      <p className="text-2xl font-black text-white italic tracking-tighter">
                        {pkg.credits?.toLocaleString()}
                      </p>
                    </div>
                    <div className="p-5 bg-green-500/5 rounded-2xl border border-green-500/10 group-hover:border-green-500/20 transition-colors">
                      <p className="text-[9px] font-black text-green-500 uppercase tracking-[0.3em] mb-2 italic">
                        Tactical_Bonus
                      </p>
                      <p className="text-2xl font-black text-green-500 italic tracking-tighter">
                        +{pkg.bonus}%
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {packages.length === 0 && !isLoading && (
          <div className="col-span-full py-24 text-center">
            <p className="text-gray-500 font-black uppercase tracking-[0.3em] italic text-xs">
              The economy matrix is currently empty.
            </p>
          </div>
        )}
      </div>

      {/* Credit Module SidePanel */}
      <SidePanel
        isOpen={isPanelOpen}
        onClose={() => setIsPanelOpen(false)}
        title={
          editingPackage ? "Alter Matrix Module" : "Define New Tactical Module"
        }
      >
        <form onSubmit={handleSubmit} className="space-y-10">
          <div className="grid grid-cols-1 gap-10">
            <div className="space-y-4">
              <label className="text-[10px] font-black text-gray-500 uppercase tracking-[0.3em] italic ml-1">
                Module_Designation
              </label>
              <input
                type="text"
                required
                className="w-full px-8 py-6 bg-white/5 border border-white/10 rounded-[1.5rem] focus:border-amber-500/50 transition-all outline-none font-black text-white italic placeholder:text-gray-700"
                placeholder="Ex. Titan Loadout"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
              />
            </div>

            <div className="grid grid-cols-2 gap-8">
              <div className="space-y-4">
                <label className="text-[10px] font-black text-gray-500 uppercase tracking-[0.3em] italic ml-1">
                  Credits_Volume
                </label>
                <input
                  type="number"
                  required
                  className="w-full px-8 py-6 bg-white/5 border border-white/10 rounded-[1.5rem] focus:border-amber-500/50 transition-all outline-none font-black text-white italic"
                  placeholder="5000"
                  value={formData.credits}
                  onChange={(e) =>
                    setFormData({ ...formData, credits: e.target.value })
                  }
                />
              </div>
              <div className="space-y-4">
                <label className="text-[10px] font-black text-gray-500 uppercase tracking-[0.3em] italic ml-1">
                  Valuation ($)
                </label>
                <input
                  type="number"
                  required
                  className="w-full px-8 py-6 bg-white/5 border border-white/10 rounded-[1.5rem] focus:border-amber-500/50 transition-all outline-none font-black text-white italic"
                  placeholder="49.99"
                  value={formData.price}
                  onChange={(e) =>
                    setFormData({ ...formData, price: e.target.value })
                  }
                />
              </div>
            </div>

            <div className="space-y-4">
              <label className="text-[10px] font-black text-gray-500 uppercase tracking-[0.3em] italic ml-1">
                Bonus_Yield (%)
              </label>
              <input
                type="number"
                className="w-full px-8 py-6 bg-white/5 border border-white/10 rounded-[1.5rem] focus:border-amber-500/50 transition-all outline-none font-black text-white italic"
                placeholder="0"
                value={formData.bonus}
                onChange={(e) =>
                  setFormData({ ...formData, bonus: e.target.value })
                }
              />
              <p className="text-[9px] text-gray-600 font-bold uppercase tracking-widest italic ml-1">
                Additional percentage credits awarded on deployment.
              </p>
            </div>
          </div>

          <div className="flex gap-6 pt-6">
            <button
              type="button"
              onClick={() => setIsPanelOpen(false)}
              className="flex-1 py-6 bg-white/5 text-gray-400 font-black rounded-2xl hover:bg-white/10 transition-all transform active:scale-95 uppercase tracking-widest text-[10px] italic border border-white/5"
            >
              Abort Initialize
            </button>
            <button
              type="submit"
              className="flex-1 py-6 bg-amber-500 text-black font-black rounded-2xl hover:bg-amber-400 shadow-[0_0_20px_rgba(245,158,11,0.2)] transition-all transform active:scale-95 uppercase tracking-widest text-[10px] italic"
            >
              {editingPackage ? "Commit Modification" : "Deploy Module"}
            </button>
          </div>
        </form>
      </SidePanel>
    </AdminLayout>
  );
};

export default CreditManagement;
